import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { supplierService } from '../../services/supplierService';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser, getLimitedUserScope } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const ExitsBySupplier = () => {
  const [reportData, setReportData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [supplierInfo, setSupplierInfo] = useState(null);
  
  // Ref para evitar llamadas duplicadas
  const hasLoadedFilters = useRef(false);

  useEffect(() => {
    if (!hasLoadedFilters.current) {
      hasLoadedFilters.current = true;
      loadFilterOptions();
    }
  }, []);

  const loadFilterOptions = async () => {
    await Promise.all([loadSuppliers(), loadWarehouses()]);
  };

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses({ active_only: true });
      if (response.success) {
        const user = authService.getCurrentUser();
        const filtered = filterWarehousesForUser(response.data || [], user);
        setWarehouses(filtered);
        if (filtered.length === 1) {
          setSelectedWarehouse(String(filtered[0].id));
        }
      }
    } catch (error) {
      console.error('Error al cargar almacenes:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar almacenes',
        type: 'error'
      });
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true });
      if (response.success) {
        setSuppliers(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar proveedores',
        type: 'error'
      });
    }
  };

  const loadReport = async () => {
    if (!selectedSupplier) {
      setToast({
        isOpen: true,
        message: 'Por favor selecciona una entidad',
        type: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      const response = await reportService.getExitsBySupplierReport(
        selectedSupplier,
        selectedWarehouse || null
      );
      
      if (response.success) {
        setReportData(response.data || []);
        setSupplierInfo(response.supplier);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el reporte',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el reporte de salidas',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (reportData.length === 0) {
      setToast({
        isOpen: true,
        message: 'No hay datos para exportar',
        type: 'warning'
      });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Salidas por Entidad');

      // Estilos
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1F4E78' }
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // Título
      worksheet.mergeCells('A1:J1');
      const titleRow = worksheet.getRow(1);
      titleRow.height = 30;
      titleRow.getCell(1).value = `Reporte de Salidas - ${supplierInfo?.name || 'Entidad'}`;
      titleRow.getCell(1).style = titleStyle;

      // Información de la entidad
      if (supplierInfo) {
        worksheet.mergeCells('A2:J2');
        const infoRow = worksheet.getRow(2);
        infoRow.getCell(1).value = `Entidad: ${supplierInfo.name} | ${supplierInfo.document_type === 'ruc' ? 'RUC' : 'DNI'}: ${supplierInfo.document_number || 'N/A'}`;
        infoRow.getCell(1).style = { alignment: { horizontal: 'left' } };
      }

      // Encabezados
      const headers = [
        'Fecha',
        'Código Guía',
        'Almacén',
        'Producto (SKU)',
        'Descripción',
        'Proyecto',
        'Cantidad',
        'Unidad',
        'Costo Unit.',
        'Total'
      ];

      const headerRow = worksheet.getRow(3);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = headerStyle;
      });
      headerRow.height = 25;

      // Datos
      reportData.forEach((row, index) => {
        const dataRow = worksheet.getRow(index + 4);
        dataRow.getCell(1).value = row.date ? formatDateLocaleLima(row.date) : '';
        dataRow.getCell(2).value = row.guide_code || '';
        dataRow.getCell(3).value = `${row.warehouse_code || ''} - ${row.warehouse_name || ''}`;
        dataRow.getCell(4).value = row.product_sku || '';
        dataRow.getCell(5).value = row.product_name || '';
        dataRow.getCell(6).value = row.project_code ? `${row.project_code} - ${row.project_name || ''}` : '';
        dataRow.getCell(7).value = parseFloat(row.quantity || 0);
        dataRow.getCell(7).numFmt = '#,##0.000';
        dataRow.getCell(8).value = row.unit_of_measure || '';
        dataRow.getCell(9).value = parseFloat(row.unit_price || 0);
        dataRow.getCell(9).numFmt = '#,##0.00';
        dataRow.getCell(10).value = parseFloat(row.total || 0);
        dataRow.getCell(10).numFmt = '#,##0.00';

        // Aplicar bordes
        for (let i = 1; i <= 10; i++) {
          dataRow.getCell(i).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        }
      });

      // Fila de totales
      const totalRow = worksheet.getRow(reportData.length + 4);
      totalRow.getCell(1).value = 'TOTAL';
      totalRow.getCell(1).style = { font: { bold: true }, alignment: { horizontal: 'right' } };
      totalRow.getCell(10).value = {
        formula: `SUM(J4:J${reportData.length + 3})`
      };
      totalRow.getCell(10).numFmt = '#,##0.00';
      totalRow.getCell(10).style = { font: { bold: true } };
      
      // Aplicar bordes a la fila de totales
      for (let i = 1; i <= 10; i++) {
        totalRow.getCell(i).border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }

      // Ajustar ancho de columnas
      worksheet.columns = [
        { width: 12 }, // Fecha
        { width: 15 }, // Código Guía
        { width: 25 }, // Almacén
        { width: 15 }, // SKU
        { width: 40 }, // Descripción
        { width: 20 }, // Proyecto
        { width: 12 }, // Cantidad
        { width: 10 }, // Unidad
        { width: 12 }, // Costo Unit.
        { width: 12 }  // Total
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Salidas_${supplierInfo?.name || 'Entidad'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Reporte exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el reporte',
        type: 'error'
      });
    }
  };

  // Calcular totales
  const totalAmount = reportData.reduce((sum, row) => sum + parseFloat(row.total || 0), 0);
  const isLimitedWithWarehouses = getLimitedUserScope(authService.getCurrentUser()).showAlmacenes;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Reporte de Salidas por Entidad</h1>
          <p className="text-gray-600 mt-1">Consulta todas las salidas realizadas por una entidad</p>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entidad (Proveedor) <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccionar entidad</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.document_type === 'ruc' ? 'RUC' : 'DNI'}: {supplier.document_number || 'N/A'})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Almacén
              </label>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {!isLimitedWithWarehouses || warehouses.length > 1 ? (
                  <option value="">
                    {isLimitedWithWarehouses ? 'Todos mis almacenes' : 'Todos los almacenes'}
                  </option>
                ) : null}
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.code} - {wh.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:col-span-2">
              <button
                onClick={loadReport}
                disabled={loading || !selectedSupplier}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Cargando...' : 'Consultar'}
              </button>
              {reportData.length > 0 && (
                <button
                  onClick={exportToExcel}
                  className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Exportar Excel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Información de la entidad */}
        {supplierInfo && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6">
            <h3 className="text-lg font-semibold text-teal-800 mb-2">Información de la Entidad</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Nombre:</span>
                <span className="ml-2 text-gray-900">{supplierInfo.name}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">{supplierInfo.document_type === 'ruc' ? 'RUC' : 'DNI'}:</span>
                <span className="ml-2 text-gray-900">{supplierInfo.document_number || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total de Salidas:</span>
                <span className="ml-2 text-gray-900">{reportData.length} registro(s)</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de resultados */}
        {reportData.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Código Guía</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Almacén</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Proyecto</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Cantidad</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Unidad</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Costo Unit.</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {row.date ? formatDateLocaleLima(row.date) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {row.guide_code || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {row.warehouse_code ? `${row.warehouse_code} - ${row.warehouse_name}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {row.product_sku || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {row.product_name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {row.project_code ? `${row.project_code} - ${row.project_name || ''}` : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {parseFloat(row.quantity || 0).toLocaleString('es-PE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                        {row.unit_of_measure || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        S/ {parseFloat(row.unit_price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        S/ {parseFloat(row.total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan="9" className="px-4 py-3 text-right text-sm text-gray-700">
                      TOTAL:
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-teal-600 text-right">
                      S/ {totalAmount.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : selectedSupplier && !loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <p className="text-gray-500">
              No se encontraron salidas para esta entidad
              {selectedWarehouse
                ? ` en el almacén seleccionado`
                : ''}
            </p>
          </div>
        ) : null}

        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default ExitsBySupplier;
